import { isSafeReturnTo, type CurrentSession } from '@easyhelp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_PREFIX, API_URL, ApiError, apiRequest } from './client';

/** Chiave della query della sessione corrente; `null` = nessuno collegato. */
export const sessionQueryKey = ['auth', 'session'] as const;

/** `GET /auth/session`: chi è collegato, oppure `null` se l'API risponde 401. */
export async function fetchSession(): Promise<CurrentSession | null> {
  try {
    return await apiRequest<CurrentSession>('/auth/session');
  } catch (error: unknown) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

/** Sessione corrente (PRD-002 US-1). Resta in cache un minuto: la shell e le guardie la condividono. */
export function useSession() {
  return useQuery({ queryKey: sessionQueryKey, queryFn: fetchSession, staleTime: 60_000 });
}

/**
 * Dove tornare dopo il login: solo un percorso relativo della web app e mai la pagina di accesso
 * stessa (eviterebbe il loop `/accesso?returnTo=/accesso`); altrimenti la radice.
 */
export function safeReturnTo(value: unknown): string {
  return isSafeReturnTo(value) && !value.startsWith('/accesso') ? value : '/';
}

/** Pagina di accesso della web app, con il percorso a cui tornare dopo il login (story #4: il link al ticket). */
export function loginPath(returnTo: string): string {
  return `/accesso?returnTo=${encodeURIComponent(safeReturnTo(returnTo))}`;
}

/** URL di avvio del login con Google, con il percorso a cui tornare dopo. */
export function loginUrl(returnTo: string): string {
  return `${API_URL}${API_PREFIX}/auth/google?returnTo=${encodeURIComponent(safeReturnTo(returnTo))}`;
}

/** `POST /auth/logout`: chiude la sessione e svuota la cache (i dati erano di quella persona). */
export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError>({
    mutationFn: () => apiRequest<void>('/auth/logout', { method: 'POST' }),
    onSettled: () => {
      queryClient.clear();
      queryClient.setQueryData(sessionQueryKey, null);
    },
  });
}
